/**
 * SearchUsersUserItem Component
 * Individual user row with avatar, name, username, and checkmark
 * Matches Flutter implementation: lib/presentation/bottom_sheet/search_users_bottom_sheet.dart - userItem
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { IdentityBadge } from '@/presentation/components/common/identity/IdentityBadge';
import { UserUtils } from '@/domain/utils/userUtils';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface SearchUsersUserItemProps {
  subscription: ExclusiveContentSubscriptionEntity;
  isSelected: boolean;
  onClick: () => void;
}

export const SearchUsersUserItem: React.FC<SearchUsersUserItemProps> = ({
  subscription,
  isSelected,
  onClick,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  return (
    <div
      {...qaAttr({
        id: `search-users-item-${subscription.userId}`,
        ...(isSelected ? { value: 'selected' } : {}),
      })}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: responsive.spacing.lg,
        cursor: 'pointer',
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(subscription.userId)}
        alt={subscription.name}
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          marginRight: responsive.spacing.base,
          objectFit: 'cover',
        }}
      />

      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xs, marginBottom: responsive.spacing.xs }}>
          <p
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              margin: 0,
            }}
          >
            {subscription.name}
          </p>
          <IdentityBadge userId={subscription.userId} />
        </div>
        <p
          style={{
            fontSize: responsive.font.sm,
            color: theme.text.secondary,
          }}
        >
          @{subscription.username} •{' '}
          {subscription.meSubscriber ?
            l10n.common.meSubscriber :
            l10n.common.subscriber}
        </p>
      </div>

      {isSelected && (
        <AssetIcon path="/assets/images/check.png" width={responsive.icon.xs} height={responsive.icon.xs} color={theme.white} />
      )}
    </div>
  );
};
